import { Injectable, inject, signal } from '@angular/core';
import { UserOnboardingService } from './user-onboarding.service';

export type TabType = 'presets' | 'quick-filters' | 'cosmetic' | 'custom-rules' | 'free-rules' | 'tier-list' | 'settings' | 'updates' | 'preview' | 'extra';

const TAB_ROUTES = new Set<TabType>([
  'presets', 'quick-filters', 'cosmetic', 'custom-rules', 'free-rules',
  'tier-list', 'settings', 'updates', 'preview', 'extra',
]);

export function tabFromPath(pathname: string): TabType | null {
  const segment = pathname.split('/')[1];
  return TAB_ROUTES.has(segment as TabType) ? (segment as TabType) : null;
}

@Injectable({
  providedIn: 'root'
})
export class TabService {
  private readonly userOnboarding = inject(UserOnboardingService);
  private _currentTab = signal<TabType>('presets');

  /**
   * Signal for the current active tab
   */
  readonly currentTab = this._currentTab.asReadonly();

  /**
   * Select a tab and update the URL
   * @param tab The tab to select
   */
  selectTab(tab: TabType, skipNavigation = false): void {
    if (this._currentTab() === tab) return;

    this._currentTab.set(tab);
    this.trackTabVisit(tab);

    // Update the URL without reloading the page
    window.history.replaceState({}, '', `/${tab}`);

    if (skipNavigation) return;

    // Only scroll if the navigation is currently sticky (user has scrolled down)
    const navigation = document.querySelector('#trade .navigation') as HTMLElement;
    if (navigation && navigation.classList.contains('sticky-active')) {
      // Find the sentinel element (the 1px div we created before the navigation)
      const sentinel = navigation.previousElementSibling as HTMLElement;
      if (sentinel) {
        // Get the sentinel's position and scroll 1px past it to stay in sticky mode
        const sentinelRect = sentinel.getBoundingClientRect();
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
        const targetScrollY = currentScrollY + sentinelRect.top + 2; // +1 to stay in sticky mode

        window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
      }
    }
  }

  /**
   * Set the current tab without updating the URL (useful for router navigation)
   * @param tab The tab to set
   */
  setTab(tab: TabType): void {
    this._currentTab.set(tab);
    this.trackTabVisit(tab);
  }

  private trackTabVisit(tab: TabType): void {
    if (tab === 'quick-filters') {
      this.userOnboarding.trackQuickFiltersVisit();
    }
  }
}

